import React from 'react';

const stats = [
  {
    value: '7+ Yrs',
    label: 'Backend Craftsmanship',
    detail: 'Node.js, Python, Go, and cloud infrastructure',
  },
  {
    value: '12+',
    label: 'Production Products Shipped',
    detail: 'Web apps, mobile apps, games, and AI systems live in production',
  },
  {
    value: '$1M+/mo',
    label: 'AI Revenue Engine',
    detail: 'Automated agent pipelines driving millions of dollars in business metrics',
  },
  {
    value: '100%',
    label: 'Async & Independent',
    detail: 'Clear communication, regular updates, zero hand-holding',
  },
];

const countries = ['USA', 'India', 'Indonesia', 'Netherlands', 'Malaysia'];

const pillars = [
  {
    icon: '🐍',
    title: 'Python-first, stack-agnostic',
    body: 'Python is my mother tongue — but I speak FastAPI, Node, Rust, Go, and whatever your codebase demands. Open to your stack.',
    color: '#00ff88',
  },
  {
  icon: '📈',
  title: 'Scratch → millions of users',
  body: 'Helped startups across the globe scale fast and stay reliable — from blank repo to production traffic that won\'t quit.',
  color: '#a78bfa',
  },
  {
  icon: '🤖',
  title: 'Recently shipped an AI agent engine',
  body: 'Built and deployed an agent engine for a client whose users generate millions in revenue every month. It runs quietly. It runs hard.',
  color: '#00d4ff',
  },
  {
    icon: '👥',
    title: 'Led multi-team delivery',
    body: 'Managed several teams shipping different products simultaneously — and shipped them on time, every time.',
    color: '#ff6b35',
  },
  {
    icon: '🛠️',
    title: 'Legacy surgery without downtime',
    body: 'Inherited spaghetti code? I untangle monolithic bottlenecks, migrate databases live, and modernise legacy tech without interrupting active users.',
    color: '#ffbe0b',
  },
  {
    icon: '🚨',
    title: 'Real-time alert systems that hit before users notice',
    body: 'Built event-driven monitoring and custom webhook engines that catch edge cases in milliseconds — notifying slack, pager, or automated fallbacks before incidents turn into churn.',
    color: '#ff3366',
  }
];

export default function NewAbout() {
  return (
    <section id="about" className="section">
      <span className="section-label animate-in">01 / About 🐍🤖🦀</span>

      {/* Hero block */}
      <div className="animate-in animate-in-delay-1" style={{ marginBottom: '3rem' }}>
        <h2 className="section-title" style={{ fontSize: 'clamp(2.25rem, 6vw, 4rem)' }}>
          I build backends that{' '}
          <span
            style={{
              background: 'linear-gradient(135deg, #00ff88, #00d4ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            survive scale.
          </span>
        </h2>
        <div className="divider" />
        <p className="section-subtitle" style={{ fontSize: '1.15rem', maxWidth: '720px' }}>
          Freelance backend & cloud engineer with{' '}
          <strong style={{ color: '#ffffff' }}>7+ years</strong> of shipping production systems.
          Python is my primary language — I can manage any stack you throw at me.
          I work with founders worldwide to take products from{' '}
          <strong style={{ color: '#00ff88' }}>scratch to millions of users</strong>.
        </p>
        <p style={{ color: '#888', marginTop: '1.25rem', fontSize: '1rem', lineHeight: '1.7', maxWidth: '720px' }}>
          Lately I've been living in the{' '}
          <strong style={{ color: '#00d4ff' }}>AI age</strong> — conversing with LLMs in the cloud,
          shipping agent engines for high-revenue clients. But Python is still there,
          handling the pipelines with care. And{' '}
          <strong style={{ color: '#ff6b35' }}>Rust</strong>? My secret sidekick for that zero-overhead thrill.
        </p>
        <p style={{ marginTop: '1.5rem' }}>
          <a
            href="#story"
            style={{
              color: '#00ff88',
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
              textUnderlineOffset: '4px',
              fontSize: '0.95rem',
            }}
          >
            Read the full tale →
          </a>
        </p>
      </div>

      {/* Stats grid */}
      <div
        className="animate-in animate-in-delay-2"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          marginBottom: '4rem',
        }}
      >
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="glass-card"
            style={{
              padding: '1.5rem 1.25rem',
              transition: 'all 0.25s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(0, 255, 136, 0.25)';
              e.currentTarget.style.transform = 'translateY(-3px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.06)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: '2.25rem',
                fontWeight: 700,
                lineHeight: 1,
                background: 'linear-gradient(135deg, #00ff88, #00d4ff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '0.5rem',
              }}
            >
              {stat.value}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#888', lineHeight: 1.4, marginBottom: '0.35rem' }}>
              {stat.label}
            </div>
            <div style={{ fontSize: '0.7rem', color: '#555', lineHeight: 1.4 }}>
              {stat.detail}
            </div>
          </div>
        ))}
      </div>

      {/* Pillars grid */}
      <div
        className="animate-in animate-in-delay-3"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem',
          marginBottom: '4rem',
        }}
      >
        {pillars.map((pillar) => (
          <div
            key={pillar.title}
            className="glass-card"
            style={{
              padding: '1.75rem',
              transition: 'all 0.3s ease',
              borderTop: `2px solid ${pillar.color}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = `${pillar.color}`;
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = `0 20px 50px rgba(0, 0, 0, 0.4), 0 0 30px ${pillar.color}15`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.06)';
              e.currentTarget.style.borderTopColor = pillar.color;
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>{pillar.icon}</div>
            <h3
              style={{
                fontSize: '1.1rem',
                fontWeight: 600,
                color: pillar.color,
                marginBottom: '0.75rem',
                lineHeight: 1.3,
              }}
            >
              {pillar.title}
            </h3>
            <p style={{ color: '#888', fontSize: '0.9rem', lineHeight: 1.7, margin: 0 }}>
              {pillar.body}
            </p>
          </div>
        ))}
      </div>

      {/* Countries */}
      <div className="animate-in animate-in-delay-4" style={{ textAlign: 'center' }}>
        <span className="section-label" style={{ textAlign: 'center' }}>
          Founders I've worked with
        </span>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '0.75rem',
            marginTop: '1rem',
          }}
        >
          {countries.map((country) => (
            <span
              key={country}
              className="glass-card"
              style={{
                padding: '0.5rem 1.25rem',
                fontSize: '0.85rem',
                color: '#ccc',
                letterSpacing: '0.02em',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(0, 255, 136, 0.3)';
                e.currentTarget.style.color = '#00ff88';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.06)';
                e.currentTarget.style.color = '#ccc';
              }}
            >
              {country}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}